import { format, parseISO } from "date-fns"

export type lift_summary = Record<string, number | string>

export type retailer_row = {
  retailer: string
  events: number
  base_wk: number
  lift: number
  plan: number | null
  m_lift: number | null
  m_lo: number | null
  m_hi: number | null
  inc_units: number
  inc_rev: number
  spend: number | null
  planned: number | null
}

export type model_row = { slice: string; term: string; lift: number; lo: number; hi: number; weeks: number }
export type curve_row = { week: number; index: number; events: number; in_promo: number }

export type kehe_event = {
  promo: string
  retailer: string
  start: string
  end: string
  weeks: number
  items: number
  lines: string
  scan: number | null
  base_wk: number
  lift: number
  plan: number | null
  extra_units: number
  extra_sales: number
  matched: number | null
  planned: number | null
  read: string
}

export type weekly_row = { retailer: string; line: string; week: string; units: number }
export type weekly_promo = { retailer: string; promo: string; start: string; end: string; lines: string }

export type kroger_deal = {
  deal: string
  promotion: string
  start: string
  end: string
  rate: number
  divisions: number
  items: number
  base_day: number
  deal_day: number
  lift: number
  units_paid: number
  extra_units: number
  spend: number
  per_extra: number
  read: string
}

export type kroger_item = { item: string; lift: number; base_day: number; units_paid: number }
export type coverage_row = { customer: string; promo_ded: number; promo_amt: number; matched_ded: number; matched_amt: number; matched_pct: number }
export type plan_band = { band: string; promos: number; planned: number; billed: number }
export type plan_row = {
  key: string
  list: string
  promo: string
  customer: string
  retailer: string
  start: string
  end: string
  planned: number
  billed: number
  ratio: number | null
  deductions: number
}
export type unbilled_row = { promo: string; customer: string; retailer: string; start: string; end: string; planned: number }

export type promo_lift_review = {
  summary: lift_summary
  kehe_retailers: retailer_row[]
  kehe_model: model_row[]
  kehe_curve: curve_row[]
  kehe_events: kehe_event[]
  kehe_weekly: weekly_row[]
  kehe_weekly_promos: weekly_promo[]
  kroger_deals: kroger_deal[]
  kroger_items: kroger_item[]
  coverage: coverage_row[]
  plan_bands: plan_band[]
  promo_plan: plan_row[]
  unbilled: unbilled_row[]
}

export const READABLE = "Readable"
export const PLAN_LISTS = ["Largest", "Over plan", "Under plan"]

export function fmt_date_range(start: string, end: string): string {
  return `${format(parseISO(start), "MMM d")} to ${format(parseISO(end), "MMM d, yyyy")}`
}

export function fmt_x(value: number): string {
  return `${value.toFixed(1)}×`
}

/** 64k, 1.29M, 812. Units, not dollars. */
export function fmt_units_short(value: number): string {
  const size = Math.abs(value)
  if (size >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (size >= 10_000) return `${(value / 1_000).toFixed(0)}k`
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value)
}

export function search_kehe_events(events: kehe_event[], query: string): kehe_event[] {
  const q = query.trim().toLowerCase()
  if (!q) return events
  return events.filter((row: kehe_event) => `${row.promo} ${row.retailer}`.toLowerCase().includes(q))
}

/** The model's slices in the order they were fitted, each with its own terms. */
export function model_slices(rows: model_row[]): { slice: string; rows: model_row[] }[] {
  const order: string[] = []
  rows.forEach((row: model_row) => {
    if (!order.includes(row.slice)) order.push(row.slice)
  })
  return order.map((slice: string) => ({ slice, rows: rows.filter((row: model_row) => row.slice === slice) }))
}

/** A range that stays above zero is a lift we can measure. */
export function is_measurable(row: { lo: number }): boolean {
  return row.lo > 0
}

/** Retailers in the weekly chart, biggest first as the snapshot lists them. */
export function weekly_retailers(rows: weekly_row[]): string[] {
  const out: string[] = []
  rows.forEach((row: weekly_row) => {
    if (!out.includes(row.retailer)) out.push(row.retailer)
  })
  return out
}

/** One retailer's weeks as chart rows: `{week, <line>: units}`, with its product lines by volume. */
export function weekly_chart(rows: weekly_row[], retailer: string): { lines: string[]; points: Record<string, number | string>[] } {
  const mine = rows.filter((row: weekly_row) => row.retailer === retailer)
  const totals = new Map<string, number>()
  mine.forEach((row: weekly_row) => totals.set(row.line, (totals.get(row.line) ?? 0) + row.units))
  const lines = [...totals.entries()].filter((entry: [string, number]) => entry[1] > 0).sort((a: [string, number], b: [string, number]) => b[1] - a[1]).map((entry: [string, number]) => entry[0])
  const by_week = new Map<string, Record<string, number | string>>()
  mine.forEach((row: weekly_row) => {
    if (!lines.includes(row.line)) return
    const point = by_week.get(row.week) ?? { week: row.week }
    point[row.line] = row.units
    by_week.set(row.week, point)
  })
  const points = [...by_week.values()].sort((a: Record<string, number | string>, b: Record<string, number | string>) => String(a.week).localeCompare(String(b.week)))
  return { lines, points }
}

/** The first and last chart week a promo touches, so a band can be drawn on a category axis. */
export function promo_span(weeks: string[], start: string, end: string): { first: string; last: string } | null {
  const touched = weeks.filter((week: string) => {
    const week_end = format(new Date(parseISO(week).getTime() + 6 * 86_400_000), "yyyy-MM-dd")
    return week <= end && week_end >= start
  })
  return touched.length ? { first: touched[0], last: touched[touched.length - 1] } : null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  describe("promo lift review model", () => {
    it("pivots one retailer's weeks by product line, biggest line first", () => {
      const rows: weekly_row[] = [
        { retailer: "Publix", line: "Entrées", week: "2026-01-11", units: 5 },
        { retailer: "Publix", line: "Crunchy Chickpeas", week: "2026-01-04", units: 40 },
        { retailer: "Publix", line: "Entrées", week: "2026-01-04", units: 10 },
        { retailer: "Publix", line: "Wraps", week: "2026-01-04", units: 0 },
        { retailer: "Meijer", line: "Entrées", week: "2026-01-04", units: 99 },
      ]
      const chart = weekly_chart(rows, "Publix")
      expect(chart.lines).toEqual(["Crunchy Chickpeas", "Entrées"])
      expect(chart.points).toEqual([
        { week: "2026-01-04", "Crunchy Chickpeas": 40, Entrées: 10 },
        { week: "2026-01-11", Entrées: 5 },
      ])
    })

    it("spans a promo across every week it touches, even by a day", () => {
      const weeks = ["2026-02-22", "2026-03-01", "2026-03-08"]
      expect(promo_span(weeks, "2026-02-26", "2026-03-04")).toEqual({ first: "2026-02-22", last: "2026-03-01" })
      expect(promo_span(weeks, "2026-04-01", "2026-04-14")).toBeNull()
    })

    it("keeps the model's slices in order and reads a range above zero as measurable", () => {
      const rows: model_row[] = [
        { slice: "Timing", term: "All promo weeks", lift: 0.27, lo: 0.18, hi: 0.37, weeks: 10 },
        { slice: "Scan depth per unit", term: "$0.72 to $1.00", lift: 0.05, lo: -0.03, hi: 0.15, weeks: 5 },
      ]
      expect(model_slices(rows).map((group: { slice: string }) => group.slice)).toEqual(["Timing", "Scan depth per unit"])
      expect(is_measurable(rows[0])).toBe(true)
      expect(is_measurable(rows[1])).toBe(false)
    })

    it("searches promos by name or retailer", () => {
      const row = { promo: "2026_Publix_Chickpea_CoOp_Jun_$3.99", retailer: "Publix" } as kehe_event
      expect(search_kehe_events([row], "chickpea")).toHaveLength(1)
      expect(search_kehe_events([row], "meijer")).toHaveLength(0)
    })

    it("shortens units without a dollar sign", () => {
      expect(fmt_units_short(1_287_502)).toBe("1.29M")
      expect(fmt_units_short(63_792)).toBe("64k")
      expect(fmt_units_short(4_536)).toBe("4,536")
    })
  })
}
